/**
 * Údržba (zámek CMS). Three jobs, all global so no screen has to opt in:
 * 1. load `GET /api/maintenance/` on app start and every 60 s;
 * 2. watch every response (typed client, authedFetch, raw fetch — one
 *    `window.fetch` hook, like admin-counts.client.ts): a 503 carrying
 *    `maintenance:true` flips the state on and shows ONE toast (throttled);
 * 3. swallow the screen's own error toast for that refused write (any
 *    `severity:'error'` toast raised within a few seconds of a maintenance
 *    503) — the one maintenance toast says it all. Unrelated errors (a wrong
 *    password on the login page…) still show.
 */
import client from '~/api/client'
import { refreshMaintenance, useMaintenanceState } from '~/composables/useMaintenance'

const POLL_MS = 60_000
const TOAST_GAP_MS = 30_000
const SWALLOW_MS = 4_000
const NOTICE = 'Úpravy jsou dočasně vypnuté — probíhá údržba'

type ToastMsg = { severity?: string; summary?: string; detail?: string; life?: number }
type ToastSvc = { add: (m: ToastMsg) => void }

export default defineNuxtPlugin((nuxtApp) => {
  const w = window as Window & { __qcmsMaintenancePatched?: boolean }
  if (w.__qcmsMaintenancePatched) return
  w.__qcmsMaintenancePatched = true

  const state = useMaintenanceState()
  const original = window.fetch.bind(window)
  let rawAdd: ((m: ToastMsg) => void) | null = null
  let lastNotice = 0
  let last503 = 0

  function notice() {
    const now = Date.now()
    if (now - lastNotice < TOAST_GAP_MS || !rawAdd) return
    lastNotice = now
    rawAdd({ severity: 'warn', summary: NOTICE, detail: state.value.message || undefined, life: 6000 })
  }

  async function inspect(res: Response) {
    if (res.status !== 503) return
    try {
      const body = await res.clone().json()
      if (body && body.maintenance) {
        last503 = Date.now()
        state.value = {
          enabled: true,
          message: body.detail || state.value.message,
          until: body.until ?? state.value.until,
        }
        notice()
      }
    } catch {
      /* not our JSON */
    }
  }

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const res = await original(input, init)
    await inspect(res)
    return res
  }
  // The typed client captured globalThis.fetch when api/client.ts was first
  // imported (before this plugin ran), so it needs its own hook.
  client.use({
    async onResponse({ response }) {
      await inspect(response)
      return response
    },
  })

  nuxtApp.hook('app:mounted', () => {
    const svc = (nuxtApp.vueApp.config.globalProperties as { $toast?: ToastSvc }).$toast
    if (!svc) return
    rawAdd = svc.add.bind(svc)
    // useToast() returns this same object, so patching the method covers every
    // `toast.add(...)` call site (web-edit alone has ~40).
    svc.add = (m: ToastMsg) => {
      if (m?.severity === 'error' && Date.now() - last503 < SWALLOW_MS) return
      rawAdd!(m)
    }
  })

  refreshMaintenance(state, original)
  setInterval(() => {
    if (document.visibilityState === 'visible') refreshMaintenance(state, original)
  }, POLL_MS)
})
